import React, { useState, useMemo } from 'react';
import { View, StyleSheet, Text, ScrollView } from 'react-native';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryGroup, VictoryLine, VictoryVoronoiContainer } from 'victory-native';
import { FencelineChartConfig as C } from './fencelineChartStyles';

interface BarChartScreenProps {
  label: string[];
  data: any;
  ChartTitle: string;
  subTitle?: string;
  legend?: any;
  Type: string;
  threshold?: number;
}

function SingleBarChartComp(props: BarChartScreenProps) {
  const [containerWidth, setContainerWidth] = useState(400);
  const n = props.data.labels.length;
  const chartWidth = n <= 30 ? containerWidth : Math.max(containerWidth, n * C.scrollPerLabel);
  const barRatio = n > 30 ? C.barRatio.dense : C.barRatio.normal;

  const transformedDatasetsAll = useMemo(() => {
    return props.data.datasets.map((dataset: any) => ({
      data: dataset.data.map((value: any, dataIndex: number) => ({
        x: props.Type === 'Single' ? props.data.labels[dataIndex] : value.x,
        y: props.Type === 'Single' ? parseFloat(value.toFixed(2)) : parseFloat(value.y.toFixed(2)),
      })),
    }));
  }, [props.data]);

  return (
    <View
      style={styles.container}
      onLayout={e => setContainerWidth(e.nativeEvent.layout.width)}
    >
      {props.data.datasets.length === 0 ? (
        <Text style={styles.noDataText}>{`No data available for ${props.ChartTitle}`}</Text>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator style={{ width: '100%' }}>
        <VictoryChart
          width={chartWidth}
          height={C.height}
          domainPadding={
            n > 10 ? { x: 10 }
              : n > 2 ? { x: 50 }
              : { x: 100 }
          }
          padding={C.padding.single}
          containerComponent={
            <VictoryVoronoiContainer labels={({ datum }) => `${datum.x}: ${datum.y.toFixed(2)}`} />
          }
        >
          <VictoryAxis
            tickFormat={t => (t.length > 10 ? `${t.slice(0, 10)}...` : t)}
            style={{ tickLabels: { fontSize: C.tickFontSize, angle: n > 5 ? 60 : 45, textAnchor: 'start' } }}
          />
          <VictoryAxis dependentAxis style={{ tickLabels: { fontSize: C.tickFontSize } }} />
          {props.threshold ? (
            <VictoryLine
              style={{ data: { stroke: 'red', strokeDasharray: '5,5' } }}
              data={[{ x: 0, y: props.threshold }, { x: props.data.labels.length, y: props.threshold }]}
            />
          ) : null}
          <VictoryGroup offset={10} colorScale="qualitative">
            {transformedDatasetsAll.map((dataset: any, index: any) => (
              <VictoryBar
                key={index}
                data={dataset.data}
                barRatio={barRatio}
                animate={{ duration: 800, onLoad: { duration: 500 } }}
                style={{ data: { stroke: props.data.datasets[index].color(1), fill: props.data.datasets[index].color(1) } }}
              />
            ))}
          </VictoryGroup>
        </VictoryChart>
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { width: '100%', alignItems: 'center' },
  noDataText: { fontSize: 14, textAlign: 'center', color: '#999', padding: 20 },
});

export default SingleBarChartComp;
